import { crc } from '~/utils/hash.utils';
import { IUserGetter } from '~/stores/base-user.store';
import { IConfigurationProvider } from '~/types/dependency.types';

export type TrackSegmentParams = Parameters<typeof window.analytics.track>;
export type TrackMixpanelParams = Parameters<typeof window.mixpanel.track>;

export type AnalyticsAdapterProps = {
    configProvider: IConfigurationProvider;
    userGetter: Pick<IUserGetter, 'currUser'>;
};

export class AnalyticsAdapter {
    configProvider: IConfigurationProvider;
    userGetter: Pick<IUserGetter, 'currUser'>;

    constructor({ configProvider, userGetter }: AnalyticsAdapterProps) {
        this.configProvider = configProvider;
        this.userGetter = userGetter;
    }

    shouldTrackMixpanel() {
        return (
            this.configProvider.config.environment === 'production' &&
            (this.userGetter.currUser?.isClient()
                ? this._shouldTrackCurrUser()
                : true)
        );
    }

    _shouldTrackCurrUser() {
        return crc(this.userGetter.currUser) % 100000 < 5 * 1000;
    }

    identifySegment(...props: Parameters<typeof window.analytics.identify>) {
        return window.analytics.identify(...props);
    }

    trackMixpanel(...props: TrackMixpanelParams) {
        window.mixpanel?.track(...props);
    }

    registerMixpanel(superPropertiesObj: {}) {
        window.mixpanel?.people.set(superPropertiesObj);
        window.mixpanel?.register(superPropertiesObj);
    }

    alias(id: string) {
        if (this.shouldTrackMixpanel()) {
            window.mixpanel?.alias(id);
        }
    }

    trackSegment(...props: TrackSegmentParams) {
        window.analytics?.track(...props);
    }

    shouldTrackSegments() {
        return this.configProvider.config.environment === 'production';
    }

    isSegmentAvailable = () =>
        !!window.analytics &&
        !!window.analytics.identify &&
        !!window.analytics.track;
}
